import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * The lander shortcut is visible only after its mechanism sentinel has passed
 * and while the one canonical purchase section remains below the viewport.
 *
 * @param {boolean} triggerPassed
 * @param {boolean} purchaseReached
 * @returns {boolean}
 */
function resolveVslStickyVisibility(triggerPassed, purchaseReached) {
  return triggerPassed && !purchaseReached;
}

class BrVslStickyCta extends HTMLElement {
  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {IntersectionObserver | null} */
  #triggerObserver = null;

  /** @type {IntersectionObserver | null} */
  #purchaseObserver = null;

  /** @type {ResizeObserver | null} */
  #barResizeObserver = null;

  /** @type {HTMLElement | null} */
  #trigger = null;

  /** @type {HTMLElement | null} */
  #purchase = null;

  /** @type {HTMLElement | null} */
  #bar = null;

  /** @type {HTMLElement | null} */
  #pageWrapper = null;

  #triggerPassed = false;
  #purchaseReached = false;

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();

    this.#trigger = this.previousElementSibling;
    this.#purchase = document.getElementById(this.dataset.targetAnchor || '');
    this.#bar = this.querySelector('[data-br-vsl-sticky-bar]');
    this.#pageWrapper = document.querySelector('.page-wrapper');

    if (
      !(this.#trigger instanceof HTMLElement) ||
      !(this.#purchase instanceof HTMLElement) ||
      !(this.#bar instanceof HTMLElement) ||
      !('IntersectionObserver' in window)
    ) {
      return;
    }

    scrollContainerMediaQuery.addEventListener('change', this.#handleScrollContainerChange, {
      signal: this.#abortController.signal,
    });
    window.addEventListener('scroll', this.#repairDocumentScrollRoot, {
      signal: this.#abortController.signal,
      passive: true,
    });
    document.addEventListener('focusin', this.#queueDocumentScrollRootRepair, {
      signal: this.#abortController.signal,
    });
    document.addEventListener('click', this.#queueDocumentScrollRootRepair, {
      signal: this.#abortController.signal,
    });
    document.addEventListener('toggle', this.#queueDocumentScrollRootRepair, {
      signal: this.#abortController.signal,
      capture: true,
    });

    if ('ResizeObserver' in window) {
      this.#barResizeObserver = new ResizeObserver(this.#syncDockOffset);
      this.#barResizeObserver.observe(this.#bar);
    }

    document.addEventListener('click', this.#handleAnchorClick, {
      signal: this.#abortController.signal,
    });

    this.#setupObservers();
    this.hidden = false;
    this.#bar.inert = true;
    this.#syncVisibility();
    this.#queueDocumentScrollRootRepair();
  }

  disconnectedCallback() {
    this.#triggerObserver?.disconnect();
    this.#purchaseObserver?.disconnect();
    this.#barResizeObserver?.disconnect();
    this.#cancelLanding();
    this.#abortController.abort();
    this.#pageWrapper?.style.removeProperty('--br-vsl-sticky-offset');
  }

  #setupObservers() {
    if (!this.#trigger || !this.#purchase) return;

    const intersectionRoot = getIntersectionRoot();

    this.#triggerObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const rootTop = entry.rootBounds?.top ?? 0;
        this.#triggerPassed = !entry.isIntersecting && entry.boundingClientRect.bottom <= rootTop;
        this.#syncVisibility();
      },
      { root: intersectionRoot }
    );
    this.#triggerObserver.observe(this.#trigger);

    this.#purchaseObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const rootBottom = entry.rootBounds?.bottom ?? window.innerHeight;
        this.#purchaseReached = entry.isIntersecting || entry.boundingClientRect.top < rootBottom;
        this.#syncVisibility();
      },
      { root: intersectionRoot }
    );
    this.#purchaseObserver.observe(this.#purchase);
  }

  #syncVisibility() {
    if (!this.#bar) return;

    const visible = resolveVslStickyVisibility(this.#triggerPassed, this.#purchaseReached);
    this.dataset.visible = String(visible);
    this.#bar.inert = !visible;
    this.#bar.setAttribute('aria-hidden', String(!visible));
    this.#syncDockOffset();
  }

  #syncDockOffset = () => {
    if (!this.#bar || !this.#pageWrapper) return;
    const visible = this.dataset.visible === 'true';
    this.#pageWrapper.style.setProperty(
      '--br-vsl-sticky-offset',
      visible ? `${this.#bar.getBoundingClientRect().height}px` : '0px'
    );
  };

  #isDesktopVslPage() {
    return (
      document.body.classList.contains('br-vsl-offer-page') &&
      scrollContainerMediaQuery.matches &&
      this.#pageWrapper instanceof HTMLElement
    );
  }

  #repairDocumentScrollRoot = () => {
    if (!this.#isDesktopVslPage()) return;
    if (
      window.scrollY === 0 &&
      document.documentElement.scrollTop === 0 &&
      document.body.scrollTop === 0
    ) {
      return;
    }

    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    } catch (_error) {
      window.scrollTo(0, 0);
    }
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  #queueDocumentScrollRootRepair = () => {
    if (!this.#isDesktopVslPage()) return;

    requestAnimationFrame(() => {
      this.#repairDocumentScrollRoot();
      requestAnimationFrame(this.#repairDocumentScrollRoot);
    });
  };

  /* ── Anchor landing ──────────────────────────────────────────────
     Every "Try now" is a plain href="#vsl-purchase", and the browser's
     native landing is correct AT THE MOMENT IT LANDS. The problem is what
     happens next: the click scrolls ~18,000px through a page whose imagery
     is lazy, so blocks above the target can gain height after the scroll
     has already been computed, and the target ends up parked well below the
     top of the viewport -- the card sitting mid-screen instead of at the top.

     Measured in Chrome at 1440 and 1721 wide and 840/1080/1300 tall, on both
     VSL pages, from several triggers, the native landing settles correctly at
     scroll-margin-top. It does not settle correctly for every environment, so
     rather than assume a cause this re-asserts the intended position after
     the scroll has stopped moving, and keeps re-asserting until the page is
     genuinely still.

     It never fights the browser (it waits for the scroll to stop before
     touching anything), never fights the customer (any wheel, touch or key
     input abandons the correction immediately), and gives up after a bounded
     window so a page that is still settling cannot trap the viewport.
     ---------------------------------------------------------------- */
  #landingFrame = 0;
  #landingAbort = null;

  #handleAnchorClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const anchorId = this.dataset.targetAnchor || '';
    if (!anchorId || !this.#purchase) return;

    const link = /** @type {HTMLElement | null} */ (event.target)?.closest?.(
      `a[href$="#${anchorId}"]`
    );
    if (!link) return;

    this.#correctLanding();
  };

  #cancelLanding = () => {
    if (this.#landingFrame) cancelAnimationFrame(this.#landingFrame);
    this.#landingFrame = 0;
    this.#landingAbort?.abort();
    this.#landingAbort = null;
  };

  #correctLanding() {
    const target = this.#purchase;
    if (!target) return;

    this.#cancelLanding();
    const abort = new AbortController();
    this.#landingAbort = abort;

    // Any deliberate input from the customer wins outright.
    for (const type of ['wheel', 'touchstart', 'keydown', 'pointerdown']) {
      window.addEventListener(type, this.#cancelLanding, {
        signal: abort.signal,
        passive: true,
      });
    }

    const scroller =
      this.#pageWrapper?.scrollHeight > this.#pageWrapper?.clientHeight ? this.#pageWrapper : null;
    const readScroll = () => (scroller ? scroller.scrollTop : window.scrollY);
    const wanted = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;

    const deadline = performance.now() + 5000;
    let lastScroll = readScroll();
    let stillFrames = 0;

    const tick = () => {
      if (abort.signal.aborted) return;

      const now = readScroll();
      stillFrames = now === lastScroll ? stillFrames + 1 : 0;
      lastScroll = now;

      // Only correct once the scroll has actually stopped, so we never
      // interrupt a smooth scroll that is still on its way.
      if (stillFrames >= 4) {
        const drift = Math.round(target.getBoundingClientRect().top - wanted);
        if (Math.abs(drift) > 2) {
          target.scrollIntoView({ block: 'start', behavior: 'instant' });
          stillFrames = 0;
        }
        // Deliberately NOT returning on the first good landing. The drift this
        // exists to fix arrives AFTER the scroll settles -- a lazy image above
        // the target finishing its load and pushing the block down. Exiting at
        // the first correct measurement is exactly what let the original bug
        // through, so keep watching until the deadline.
      }

      if (performance.now() > deadline) {
        this.#cancelLanding();
        return;
      }
      this.#landingFrame = requestAnimationFrame(tick);
    };

    this.#landingFrame = requestAnimationFrame(tick);
  }

  #handleScrollContainerChange = () => {
    this.#triggerObserver?.disconnect();
    this.#purchaseObserver?.disconnect();
    this.#triggerPassed = false;
    this.#purchaseReached = false;
    this.#syncVisibility();
    this.#setupObservers();
  };
}

if (!customElements.get('br-vsl-sticky-cta')) {
  customElements.define('br-vsl-sticky-cta', BrVslStickyCta);
}

export { resolveVslStickyVisibility };
